/**
 * Pane-header en de kleine bouwstenen erin (review §4.2; pane-schil fase 1, 21 september 2026).
 *
 * Eén titelbalk voor alle panes: links de titel (zonder beeldmerk, besluit Asil 2 oktober 2026)
 * en een context in grijs; rechts eventueel voortgang of eigen acties, en altijd op dezelfde plek de taalknop
 * NL/EN (als de pane er een heeft) en een ⋯-menu met Herladen, Over en de
 * pane-instellingen die de aanroeper meegeeft. Daaronder een haarlijn en een accentlijn in
 * het AGTT-blauw; Snap en Sense krijgen dezelfde balk met hun merkverloop als variant.
 *
 *   <PaneHeader title="Test of Details" context="OpEx"
 *               language={{ lang, onToggle }} menu={[{ label: 'Toetsen…', onClick }]} />
 *
 * De balk trekt zich met negatieve marges uit de binnenmarge van de pane; de pane zet
 * daarvoor `--pane-gutter` en `--pane-gutter-top` op zijn container.
 *
 * Het merk staat sinds 2 oktober 2026 als vaag watermerk in het werkgebied: de kop zet
 * `data-pane-watermark` op de scrollende inhoud van de pane (of zijn ouder), en pane.css geeft die
 * container het logo als achtergrond, gecentreerd en vast in beeld. Geen extra element, dus geen
 * verschuiving van de opbouw en niets dat klikken onderschept.
 */

import * as React from "react";
import { shellText } from "./shellText";
import { useLang } from "../../i18n/LangContext";
import "./pane.css";
import { useKeepInPane } from "./useKeepInPane";

declare const __SMARTSUITE_VERSION__: string | undefined;
declare const __SMARTSUITE_BUILD__: string | undefined;

export interface PaneProgress {
  completed: number;
  total: number;
}

export interface PaneMenuItem {
  label: string;
  onClick: () => void;
  title?: string;
  disabled?: boolean;
  /** Ingedrukte stand (bijv. een instelling die aan staat). */
  checked?: boolean;
}

export interface PaneHeaderProps {
  /** Naam van de pane; bij Documentation het actieve tabblad. */
  title: string;
  /** Actieve context: geselecteerd document, gekozen bron. Klein en grijs. */
  context?: string;
  /** Compacte voortgang; alleen getoond als total > 0. */
  progress?: PaneProgress;
  /** Eigen acties rechts (icoonknoppen, segmented control), vóór taal en menu. */
  actions?: React.ReactNode;
  /** Vervallen (2 oktober 2026): de titelbalk heeft geen beeldmerk meer; wordt genegeerd. */
  logo?: React.ReactNode | null;
  /** Vaag merk als achtergrond van het werkgebied: AGTT (standaard), DocSense, of geen (`false`). */
  watermark?: PaneWatermark;
  /** Accentlijn onder de balk: AGTT-blauw (standaard), het DocSense-verloop, of geen. */
  accent?: boolean | "brand";
  /** Taalknop NL/EN rechts; weglaten als de pane geen vertaling heeft. */
  language?: { lang: "NL" | "EN"; onToggle: () => void; title?: string };
  /** Pane-eigen regels in het ⋯-menu, boven de vaste regels Herladen en Over. */
  menu?: PaneMenuItem[];
  /** Vervallen (4 oktober 2026, besluit Asil B5): geen Help-regel tot er hulppagina's zijn; wordt genegeerd. */
  helpUrl?: string;
  /** ⋯-menu helemaal weglaten (alleen voor panes zonder chrome, zoals de portable viewer). */
  noMenu?: boolean;
  className?: string;
}

export type PaneWatermark = "agtt" | "docsense" | false;

/** Dichtstbijzijnde scrollende voorouder van de kop (het werkgebied), of de directe ouder. */
function watermarkHost(bar: HTMLElement | null): HTMLElement | null {
  const parent = bar?.parentElement ?? null;
  for (let el = parent; el && el !== document.body; el = el.parentElement) {
    const oy = window.getComputedStyle(el).overflowY;
    if (oy === "auto" || oy === "scroll") return el;
  }
  return parent;
}

export function PaneHeader({
  title,
  context,
  progress,
  actions,
  accent = true,
  language,
  menu,
  noMenu,
  className,
  watermark = "agtt",
}: PaneHeaderProps): JSX.Element {
  const hasProgress = !!progress && progress.total > 0;
  const showRight = !!actions || !!language || !noMenu;
  const barRef = React.useRef<HTMLDivElement>(null);
  // Taal van de vaste regels: de taalknop van de pane, anders de provider of de taal van Excel.
  const ctxLang = useLang();
  const lang = language?.lang ?? ctxLang;
  // Het watermerk hoort bij het werkgebied, niet bij de kop: zet het op de scrollende inhoud waar de kop
  // in staat (Documentation en Questions hebben nog een kopwrapper ertussen), anders op de directe ouder.
  // Een data-attribuut, zodat React de className van die container gewoon kan blijven beheren.
  React.useLayoutEffect(() => {
    const host = watermarkHost(barRef.current);
    if (!host || !watermark) return undefined;
    host.setAttribute("data-pane-watermark", watermark);
    return () => {
      host.removeAttribute("data-pane-watermark");
    };
  }, [watermark]);
  return (
    <div className={`pane-header-bar${className ? ` ${className}` : ""}`} ref={barRef}>
      <div className="pane-header">
        <h2 className="pane-header__title" title={title}>
          {title}
        </h2>
        {context ? (
          <span className="pane-header__context" title={context}>
            {context}
          </span>
        ) : null}
        <span className="pane-header__spacer" />
        {hasProgress ? (
          <>
            <VDivider />
            <span className="pane-header__spacer" />
            <SegmentedProgress completed={progress!.completed} total={progress!.total} />
            <span className="pane-header__spacer" />
          </>
        ) : null}
        {showRight ? (
          <div className="pane-header__actions">
            {actions}
            {actions && (language || !noMenu) ? <VDivider /> : null}
            {language ? (
              <button
                type="button"
                className="pane-icon-btn pane-header__lang"
                onClick={language.onToggle}
                title={language.title ?? shellText("switchLang", language.lang)}
                aria-label={language.title ?? shellText("switchLang", language.lang)}
              >
                <span className="pane-header__lang-code">{language.lang}</span>
              </button>
            ) : null}
            {!noMenu ? <PaneMoreMenu items={menu} paneTitle={title} lang={lang} /> : null}
          </div>
        ) : null}
      </div>
      {accent ? (
        <div
          className={`pane-header__accent${accent === "brand" ? " is-brand" : ""}`}
          aria-hidden="true"
        />
      ) : null}
    </div>
  );
}

/** Versie zoals webpack die meegeeft (manifestversie en bouwdatum), of leeg in tests. */
export function smartSuiteVersion(): { version: string; build: string } {
  const version = typeof __SMARTSUITE_VERSION__ === "string" ? __SMARTSUITE_VERSION__ : "";
  const build = typeof __SMARTSUITE_BUILD__ === "string" ? __SMARTSUITE_BUILD__ : "";
  return { version, build };
}

/**
 * Het ⋯-menu rechts in de titelbalk: pane-eigen regels bovenaan, daaronder altijd Herladen
 * en Over. Opent onder de knop, sluit met Esc of een klik erbuiten.
 * De vaste regels volgen de taalknop van de pane (knoppenmatrix 4 oktober 2026), anders de taal van de
 * provider of van Excel (Store-voorbereiding, 5 oktober 2026).
 */
function PaneMoreMenu({
  items,
  paneTitle,
  lang,
}: {
  items?: PaneMenuItem[];
  paneTitle: string;
  lang?: "NL" | "EN";
}): JSX.Element {
  const en = lang === "EN";
  const [open, setOpen] = React.useState(false);
  const [about, setAbout] = React.useState(false);
  const rootRef = React.useRef<HTMLSpanElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  // Binnen de pane houden, ook op 320 px (ST14, #2752); opnieuw als "Over" uitklapt.
  useKeepInPane(menuRef, open, about);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent): void => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const run = (fn: () => void): void => {
    setOpen(false);
    fn();
  };
  const { version, build } = smartSuiteVersion();
  const own = items ?? [];

  return (
    <span className="pane-header__more" ref={rootRef}>
      <button
        type="button"
        className={`pane-icon-btn${open ? " is-active" : ""}`}
        onClick={() => setOpen((v) => !v)}
        title={en ? "More options" : "Meer opties"}
        aria-label={en ? "More options" : "Meer opties"}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
          <circle cx="3" cy="8" r="1.4" fill="currentColor" />
          <circle cx="8" cy="8" r="1.4" fill="currentColor" />
          <circle cx="13" cy="8" r="1.4" fill="currentColor" />
        </svg>
      </button>
      {open ? (
        <div
          ref={menuRef}
          className="pane-header__menu"
          role="menu"
          aria-label={`Menu ${paneTitle}`}
        >
          {own.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className={`pane-header__menuitem${item.checked ? " is-checked" : ""}`}
              disabled={item.disabled}
              title={item.title}
              onClick={() => run(item.onClick)}
            >
              {item.checked !== undefined ? (
                <span className="pane-header__check" aria-hidden="true">
                  {item.checked ? "✓" : ""}
                </span>
              ) : null}
              {item.label}
            </button>
          ))}
          {own.length > 0 ? <div className="pane-header__menusep" role="separator" /> : null}
          <button
            type="button"
            role="menuitem"
            className="pane-header__menuitem"
            title={en ? "Reload the pane" : "De pane opnieuw laden"}
            onClick={() => run(() => window.location.reload())}
          >
            {en ? "Reload" : "Herladen"}
          </button>
          <button
            type="button"
            role="menuitem"
            className="pane-header__menuitem"
            title={en ? "Show version and build" : "Versie en build tonen"}
            onClick={() => {
              setAbout((v) => !v);
            }}
            aria-expanded={about}
          >
            {en ? "About" : "Over"}
          </button>
          {about ? (
            <div className="pane-header__about">
              AGTT SmartSuite · {paneTitle}
              {version ? ` · ${en ? "version" : "versie"} ${version}` : ""}
              {build ? ` · build ${build}` : ""}
            </div>
          ) : null}
        </div>
      ) : null}
    </span>
  );
}

/** Dunne verticale scheiding tussen de blokken van een kop of actiebalk. */
export function VDivider(): JSX.Element {
  return <span className="pane-vdivider" aria-hidden="true" />;
}

/** Platte icoonknop in lint-stijl. */
export function HeaderIconBtn({
  title,
  onClick,
  disabled,
  active,
  children,
}: {
  title: string;
  onClick: () => void;
  disabled?: boolean;
  /** Ingedrukte stand, bijvoorbeeld een filter dat aan staat. */
  active?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      className={`pane-icon-btn${active ? " is-active" : ""}`}
      onClick={onClick}
      title={title}
      aria-label={title}
      aria-pressed={active === undefined ? undefined : active}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

/**
 * Gesegmenteerde voortgang: gelijke strepen (max 5) die groen worden. Eén streep
 * per stap tot en met 5, daarboven proportioneel.
 */
export function SegmentedProgress({
  completed,
  total,
  maxSegments = 5,
  showLabel = true,
}: {
  completed: number;
  total: number;
  maxSegments?: number;
  showLabel?: boolean;
}): JSX.Element {
  const lang = useLang();
  const segs = total > 0 ? Math.min(total, maxSegments) : maxSegments;
  const filled =
    total > 0 ? (segs === total ? completed : Math.round((completed / total) * segs)) : 0;
  return (
    <div
      className="pane-progress"
      role="img"
      aria-label={`${completed} ${shellText("of", lang)} ${total}`}
    >
      <div className="pane-progress__segments">
        {Array.from({ length: segs }).map((_, i) => (
          <div key={i} className={`pane-progress__segment${i < filled ? " is-filled" : ""}`} />
        ))}
      </div>
      {showLabel ? (
        <span className="pane-progress__label">
          {completed}/{total}
        </span>
      ) : null}
    </div>
  );
}
